import {
  loginSchema,
  registerSchema,
  SPORT_IDS,
  WAITLIST_TYPES,
  waitlistReservationSchema,
  type WaitlistType,
} from '@ownermode/shared';
import { useState, type FormEvent } from 'react';
import { Link, Navigate, useLocation, useNavigate, useSearchParams } from 'react-router';
import { SocialSignIn } from '../components/SocialSignIn';
import { Alert, Button, Crest, Field } from '../components/ui';
import { api, ApiError } from '../lib/api';
import { useAuth } from '../lib/auth';
import { usePublicConfig } from '../lib/config';

function AuthCard({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle: string;
  children: React.ReactNode;
}) {
  return (
    <div className="mx-auto flex max-w-md flex-col px-4 py-16">
      <div className="mb-8 flex flex-col items-center text-center">
        <Crest className="h-12 w-12" />
        <h1 className="mt-4 text-3xl">{title}</h1>
        <p className="mt-2 text-silver">{subtitle}</p>
      </div>
      <div className="panel p-6 sm:p-8">{children}</div>
    </div>
  );
}

type FieldErrors = Record<string, string>;

function zodErrors(issues: { path: PropertyKey[]; message: string }[]): FieldErrors {
  return Object.fromEntries(issues.map((i) => [String(i.path[0]), i.message]));
}

export function Login() {
  const { login, status, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [errors, setErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // A guest may sign in to an existing account; their guest franchise is kept aside for them.
  if (status === 'signed_in' && !user?.isGuest) return <Navigate to="/dashboard" replace />;

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const data = Object.fromEntries(new FormData(e.currentTarget));
    const parsed = loginSchema.safeParse(data);
    if (!parsed.success) return setErrors(zodErrors(parsed.error.issues));
    setErrors({});
    setFormError(null);
    setBusy(true);
    try {
      await login(parsed.data);
      navigate((location.state as { from?: string } | null)?.from ?? '/dashboard', {
        replace: true,
      });
    } catch (err) {
      setFormError(err instanceof ApiError ? err.message : 'Could not sign in. Try again.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthCard title="Welcome back" subtitle="Your boardroom is waiting.">
      <div className="mb-5">
        <SocialSignIn
          onSignedIn={() =>
            navigate((location.state as { from?: string } | null)?.from ?? '/dashboard', {
              replace: true,
            })
          }
        />
      </div>
      <form onSubmit={onSubmit} className="space-y-4" noValidate>
        {formError ? <Alert>{formError}</Alert> : null}
        <Field
          label="Email"
          name="email"
          type="email"
          autoComplete="email"
          required
          error={errors.email}
        />
        <Field
          label="Password"
          name="password"
          type="password"
          autoComplete="current-password"
          required
          error={errors.password}
        />
        <p className="-mt-1 text-right text-sm">
          <Link to="/forgot-password" className="text-gold-bright underline underline-offset-2">
            Forgot password?
          </Link>
        </p>
        <Button type="submit" className="w-full" disabled={busy}>
          {busy ? 'Signing in…' : 'Sign in'}
        </Button>
      </form>
      <p className="mt-6 text-center text-sm text-slate">
        New owner?{' '}
        <Link to="/register" className="text-gold-bright underline underline-offset-2">
          Create an account
        </Link>
      </p>
    </AuthCard>
  );
}

export function Register() {
  const { register, status, user } = useAuth();
  const config = usePublicConfig();
  const { appMode } = config;
  const navigate = useNavigate();
  const [inviteCode, setInviteCode] = useState('');
  const [errors, setErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // A guest registers by saving the franchise they already have.
  if (user?.isGuest) return <Navigate to="/save" replace />;
  if (status === 'signed_in') return <Navigate to="/dashboard" replace />;

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const raw = Object.fromEntries(new FormData(e.currentTarget)) as Record<string, string>;
    const parsed = registerSchema.safeParse({ ...raw, inviteCode: raw.inviteCode || undefined });
    if (!parsed.success) return setErrors(zodErrors(parsed.error.issues));
    setErrors({});
    setFormError(null);
    setBusy(true);
    try {
      await register(parsed.data);
      navigate('/dashboard', { replace: true });
    } catch (err) {
      setFormError(
        err instanceof ApiError ? err.message : 'Could not create your account. Try again.',
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthCard title="Claim your franchise" subtitle="Create your owner account.">
      {config.data?.registrationPaused ? (
        <div className="mb-4">
          <Alert tone="info">
            New sign-ups are paused for a short while. Existing owners can still{' '}
            <Link to="/login" className="underline">
              sign in
            </Link>
            .
          </Alert>
        </div>
      ) : appMode === 'waitlist' ? (
        <div className="mb-4">
          <Alert tone="info">
            {config.data?.accessMode === 'development'
              ? "We're building in public. Accounts are limited to testers for now — "
              : "We're in private beta. Registration needs an approved reservation or an invite code. "}
            {config.data?.reservationsEnabled !== false ? (
              <Link to="/waitlist" className="underline">
                Reserve a seat
              </Link>
            ) : (
              <Link to="/#updates" className="underline">
                follow the build
              </Link>
            )}
            .
          </Alert>
        </div>
      ) : null}
      <div className="mb-5">
        <SocialSignIn
          inviteCode={inviteCode}
          onSignedIn={() => navigate('/dashboard', { replace: true })}
        />
      </div>
      <form onSubmit={onSubmit} className="space-y-4" noValidate>
        {formError ? <Alert>{formError}</Alert> : null}
        <Field
          label="Owner name"
          name="displayName"
          autoComplete="nickname"
          required
          error={errors.displayName}
        />
        <Field
          label="Email"
          name="email"
          type="email"
          autoComplete="email"
          required
          error={errors.email}
        />
        <Field
          label="Password"
          name="password"
          type="password"
          autoComplete="new-password"
          required
          error={errors.password}
          hint="At least 10 characters, with a letter and a number."
        />
        <Field
          label={appMode === 'waitlist' ? 'Invite code (optional)' : 'Referral code (optional)'}
          hint={
            appMode === 'waitlist'
              ? undefined
              : 'Invited by a friend? Enter their code. Otherwise leave it blank.'
          }
          name="inviteCode"
          value={inviteCode}
          onChange={(e) => setInviteCode(e.target.value)}
          autoComplete="off"
          error={errors.inviteCode}
        />
        <Button type="submit" className="w-full" disabled={busy}>
          {busy ? 'Creating account…' : 'Create account'}
        </Button>
      </form>
      <p className="mt-6 text-center text-sm text-slate">
        Already an owner?{' '}
        <Link to="/login" className="text-gold-bright underline underline-offset-2">
          Sign in
        </Link>
      </p>
    </AuthCard>
  );
}

const RESERVATION_TYPES: Record<WaitlistType, { label: string; hint: string }> = {
  league_host: { label: 'Host a league', hint: 'Run a private league and invite friends.' },
  solo_owner: { label: 'Play solo', hint: 'Own one franchise against CPU owners.' },
  free_agent_owner: {
    label: "Join someone's league",
    hint: 'Take a franchise in a league a friend hosts.',
  },
};
const SPORT_LABELS: Record<(typeof SPORT_IDS)[number], string> = {
  football: 'Football',
  basketball: 'Basketball',
  soccer: 'Soccer',
};

/**
 * Reserve a beta seat. The server answers the same whether the email is new, already on the
 * list or already an account, so this always ends on the same confirmation.
 */
export function Waitlist() {
  const config = usePublicConfig();
  const [params] = useSearchParams();
  const [type, setType] = useState<WaitlistType>('solo_owner');
  const [sports, setSports] = useState<string[]>([]);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  if (config.data?.accessMode === 'public')
    return (
      <AuthCard title="No reservation needed" subtitle="Registration is open to everyone.">
        <Link to="/register">
          <Button className="w-full">Create an account</Button>
        </Link>
      </AuthCard>
    );
  if (config.data && !config.data.reservationsEnabled)
    return (
      <AuthCard
        title="Reservations are closed"
        subtitle="We're not taking new reservations right now."
      >
        <p className="text-sm text-silver">
          Have an invite code?{' '}
          <Link to="/register" className="text-gold-bright underline underline-offset-2">
            Create your account
          </Link>
          .
        </p>
      </AuthCard>
    );
  if (done)
    return (
      <AuthCard title="You're on the list" subtitle="Thanks for reserving a seat.">
        <p className="text-sm text-silver">
          We let owners in a few at a time. When your seat is ready we'll email you, and you can
          create your account with the same email address.
        </p>
        <Link to="/" className="mt-6 inline-block text-gold-bright underline underline-offset-2">
          Back to the homepage
        </Link>
      </AuthCard>
    );

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const raw = Object.fromEntries(new FormData(e.currentTarget)) as Record<string, string>;
    const parsed = waitlistReservationSchema.safeParse({
      email: raw.email,
      displayName: raw.displayName,
      type,
      sports,
      leagueName: type === 'league_host' && raw.leagueName ? raw.leagueName : undefined,
      referralCode: params.get('ref') || undefined,
    });
    if (!parsed.success) {
      const errs = zodErrors(parsed.error.issues);
      if (errs.sports) errs.sports = 'Pick at least one sport.';
      return setErrors(errs);
    }
    setErrors({});
    setFormError(null);
    setBusy(true);
    try {
      await api('/api/waitlist', { body: parsed.data, auth: false });
      setDone(true);
    } catch (err) {
      setFormError(
        err instanceof ApiError ? err.message : 'Could not save your reservation. Try again.',
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthCard
      title="Reserve a seat"
      subtitle="The beta is invite-only. Join the waitlist and we'll email you when your seat is ready."
    >
      <form onSubmit={onSubmit} className="space-y-4" noValidate>
        {formError ? <Alert>{formError}</Alert> : null}
        <Field
          label="Owner name"
          name="displayName"
          autoComplete="nickname"
          required
          error={errors.displayName}
        />
        <Field
          label="Email"
          name="email"
          type="email"
          autoComplete="email"
          required
          error={errors.email}
          hint="Your invitation goes here. Register with this address."
        />
        <fieldset className="space-y-2">
          <legend className="text-sm font-medium text-silver">How do you want to play?</legend>
          {WAITLIST_TYPES.map((t) => (
            <label
              key={t}
              className={`flex cursor-pointer gap-3 rounded-lg border px-3.5 py-2.5 ${
                type === t ? 'border-gold/60 bg-gold/5' : 'border-overlay/10'
              }`}
            >
              <input
                type="radio"
                name="type"
                value={t}
                checked={type === t}
                onChange={() => setType(t)}
                className="mt-1 accent-gold"
              />
              <span>
                <span className="block text-sm text-paper">{RESERVATION_TYPES[t].label}</span>
                <span className="block text-xs text-slate">{RESERVATION_TYPES[t].hint}</span>
              </span>
            </label>
          ))}
        </fieldset>
        {type === 'league_host' ? (
          <Field
            label="League name (optional)"
            name="leagueName"
            maxLength={60}
            error={errors.leagueName}
          />
        ) : null}
        <fieldset aria-describedby={errors.sports ? 'sports-error' : undefined}>
          <legend className="text-sm font-medium text-silver">Sports you'd play</legend>
          <div className="mt-2 flex flex-wrap gap-2">
            {SPORT_IDS.map((s) => (
              <label
                key={s}
                className={`flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-2 text-sm ${
                  sports.includes(s)
                    ? 'border-gold/60 bg-gold/5 text-paper'
                    : 'border-overlay/10 text-silver'
                }`}
              >
                <input
                  type="checkbox"
                  checked={sports.includes(s)}
                  onChange={(e) =>
                    setSports((cur) =>
                      e.target.checked ? [...cur, s] : cur.filter((x) => x !== s),
                    )
                  }
                  className="accent-gold"
                />
                {SPORT_LABELS[s]}
              </label>
            ))}
          </div>
          {errors.sports ? (
            <p id="sports-error" className="mt-1.5 text-sm text-negative">
              {errors.sports}
            </p>
          ) : null}
        </fieldset>
        <p className="text-xs text-slate">
          We keep your name, email and these answers to run the waitlist. See the{' '}
          <Link to="/privacy" className="text-gold-bright underline underline-offset-2">
            privacy policy
          </Link>
          .
        </p>
        <Button type="submit" className="w-full" disabled={busy}>
          {busy ? 'Reserving…' : 'Reserve my seat'}
        </Button>
      </form>
      <p className="mt-6 text-center text-sm text-slate">
        Already invited?{' '}
        <Link to="/register" className="text-gold-bright underline underline-offset-2">
          Create your account
        </Link>
      </p>
    </AuthCard>
  );
}
